import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, UtensilsCrossed, ChevronLeft, ChevronRight, Utensils, Coffee, PartyPopper, Building2, Heart, Sparkles, ArrowRight } from 'lucide-react';
import greenImg from "../assets/green.png";
import wheet from "../assets/magomeal_wheet.png";
import eco from "../assets/Eco-Friendly.png";
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import HomeCarousel from '../components/HomeCarousel';
import SummerSale, { SaleStrip } from '../components/SummerSale';

// Import cutlery images
import tastymaxi from "../assets/Cutlery/tastymaxi.jpg";
import tastymaxi_6 from "../assets/Cutlery/tastymaxi_6.jpg";
import tastymaxi_7 from "../assets/Cutlery/tastymaxi_7.jpg";
import tastymaxi_8 from "../assets/Cutlery/tastymaxi_8.jpg";
import tastymaxi_88 from "../assets/Cutlery/tastymaxi_88.jpg";
import tastymaxi_9 from "../assets/Cutlery/tastymaxi_9.jpg";
import tastymaxi_10 from "../assets/Cutlery/tastymaxi_10.jpg";
import tastymaxi_11 from "../assets/Cutlery/tastymaxi_11.jpg";
import tastymaxi_12 from "../assets/Cutlery/tastymaxi_12.jpg";
import tastymaxi_13 from "../assets/Cutlery/tastymaxi_13.jpg";
import tastypixy from "../assets/Cutlery/tastypixy.jpg";

// Import cup images
import cup1 from "../assets/cup/c1.jpg";
import cup2 from "../assets/cup/c2.jpg";
import cup3 from "../assets/cup/c9.jpg";
import cup4 from "../assets/cup/c4.jpg";
import cup5 from "../assets/cup/c5.jpg";
import cup6 from "../assets/cup/c6.jpg";
import cup10 from "../assets/cup/c10.jpg";
import cup11 from "../assets/cup/c11.jpg";

// Import local images
import edibleCupsImg from "../assets/images/home_edible_cups.jpg";
import cutleryImg from "../assets/images/home_cutlery.jpg";
import recycleImg from "../assets/images/home_recycle.jpg";
import heroCupsImg from "../assets/images/hero_cups.jpg";
import heroCutleryImg from "../assets/images/hero_cutlery.jpg";
import logoSvg from "../assets/images/magomeal_logo.svg";

// Import endorsement media
import simoneImg from "../assets/endorsement/simone.jpg";
import simoneVideo from "../assets/endorsement/simone.mp4";

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const useCaseCarouselRef = useRef(null);
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  // Use Cases for Collaboration Section
  const useCases = [
    {
      id: 'restaurants',
      icon: <Utensils size={24} />,
      label: 'Restaurants',
      description: 'Elevate the dining experience with sustainable tableware',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop',
      color: '#004534'
    },
    {
      id: 'coffee-shops',
      icon: <Coffee size={24} />,
      label: 'Coffee Shops',
      description: 'Stand out with innovative, waste-free drinkware',
      image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&h=400&fit=crop',
      color: '#6B4423'
    },
    {
      id: 'festivals',
      icon: <PartyPopper size={24} />,
      label: 'Festivals & Events',
      description: 'Add a sustainable, conversation-starting touch',
      image: 'https://images.unsplash.com/photo-1509315811345-672d83ef2fbc?w=600&h=400&fit=crop',
      color: '#FF6B35'
    },
    {
      id: 'corporate',
      icon: <Building2 size={24} />,
      label: 'Corporate Events',
      description: 'Demonstrate real commitment to sustainability',
      image: 'https://images.pexels.com/photos/7648472/pexels-photo-7648472.jpeg?w=600&h=400&fit=crop',
      color: '#1E3A5F'
    },
    {
      id: 'weddings',
      icon: <Heart size={24} />,
      label: 'Weddings',
      description: 'Turn ordinary moments into unforgettable memories',
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop',
      color: '#D4A574'
    },
    {
      id: 'brands',
      icon: <Sparkles size={24} />,
      label: 'Brand Activations',
      description: 'Create memorable experiential campaigns',
      image: 'https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=600&h=400&fit=crop',
      color: '#8B5CF6'
    }
  ];

  // Scroll carousel to a specific card
  const scrollToCard = (index) => {
    if (useCaseCarouselRef.current) {
      const cardWidth = 336; // 320px card + 16px gap
      useCaseCarouselRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
    setActiveUseCase(index);
  };

  // Scroll carousel left/right
  const scrollCarousel = (direction) => {
    const newIndex = direction === 'left'
      ? Math.max(0, activeUseCase - 1)
      : Math.min(useCases.length - 1, activeUseCase + 1);
    scrollToCard(newIndex);
  };

  // Track scroll position to update dots
  useEffect(() => {
    const carousel = useCaseCarouselRef.current;
    if (!carousel) return;

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = carousel.scrollLeft;
        const cardWidth = 336;
        const newActive = Math.round(scrollLeft / cardWidth);
        if (newActive >= 0 && newActive < useCases.length && newActive !== activeUseCase) {
          setActiveUseCase(newActive);
        }
      }, 100);
    };

    carousel.addEventListener('scroll', handleScroll);
    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [useCases.length, activeUseCase]);

  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Edible Cups features
  const edibleCupsFeatures = [
    {
      icon: <img src={edibleCupsImg} alt={t('home.edibleCupTitle')} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "12px" }} />,
      title: t('home.edibleCupTitle'),
      description: t('home.edibleCupDesc')
    },
    {
      icon: <img src={wheet} alt={t('home.sustainableMaterials')} style={{ width: "80px", height: "80px", objectFit: "contain" }} />,
      title: t('home.sustainableMaterials'),
      description: t('home.sustainableMaterialsDesc')
    },
    {
      icon: <img src={eco} alt={t('home.ecoFriendlyTitle')} style={{ width: "80px", height: "80px", objectFit: "contain" }} />,
      title: t('home.ecoFriendlyTitle'),
      description: t('home.ecoFriendlyDescLong')
    }
  ];

  // Cutlery & Tableware features
  const cutleryFeatures = [
    {
      icon: <UtensilsCrossed size={60} strokeWidth={1.5} style={{ color: 'var(--brand-dark)' }} />,
      title: t('home.sustainableTableware'),
      description: t('home.sustainableTablewareDesc')
    },
    {
      icon: <img src={cutleryImg} alt={t('home.edibleCutlery')} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "12px" }} />,
      title: t('home.edibleCutlery'),
      description: t('home.edibleCutleryDesc')
    },
    {
      icon: <img src={recycleImg} alt={t('home.reducedPlastic')} style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "12px" }} />,
      title: t('home.reducedPlastic'),
      description: t('home.reducedPlasticDesc')
    }
  ];

  // Edible Cups carousel images
  const edibleCupsCarouselImages = [
    { url: cup10, alt: "Edible waffle cup" },
    { url: cup2, alt: "Edible waffle cup" },
    { url: cup3, alt: "Edible waffle cup" },
    { url: cup4, alt: "Edible waffle cup" },
    { url: cup5, alt: "Edible waffle cup" },
    { url: cup6, alt: "Edible waffle cup" },
    { url: cup1, alt: "Edible waffle cup" },
    { url: cup11, alt: "Edible waffle cup" },
  ];

  // Cutlery carousel images
  const cutleryCarouselImages = [
    { url: tastymaxi, alt: "Eco-friendly forks" },
    { url: tastymaxi_6, alt: "Eco-friendly forks" },
    { url: tastymaxi_7, alt: "Eco-friendly forks" },
    { url: tastymaxi_8, alt: "Eco-friendly forks" },
    { url: tastymaxi_88, alt: "Eco-friendly forks" },
    { url: tastymaxi_9, alt: "Eco-friendly forks" },
    { url: tastymaxi_10, alt: "Eco-friendly forks" },
    { url: tastymaxi_11, alt: "Eco-friendly forks" },
    { url: tastymaxi_12, alt: "Eco-friendly forks" },
    { url: tastymaxi_13, alt: "Eco-friendly forks" },
    { url: tastypixy, alt: "Eco-friendly forks" },
  ];

  return (
    <div className="home-page">
      {/* 🔆 Summer Sale announcement strip */}
      <SaleStrip />

      {/* 🌐 Social Media Icons */}
      <section className="social-media-section" style={{ marginBottom: "0", paddingBottom: "0" }}>
        <div className="container">
          <div className="social-icons-container">
            <a href="https://www.facebook.com/profile.php?id=61580398874827" target="_blank" rel="noopener noreferrer" className="social-icon facebook-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/magomeal" target="_blank" rel="noopener noreferrer" className="social-icon linkedin-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
            <a href="https://www.instagram.com/magomeal.official/" target="_blank" rel="noopener noreferrer" className="social-icon instagram-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a href="https://wa.me/31628253218" target="_blank" rel="noopener noreferrer" className="social-icon whatsapp-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
            </a>
            <a href="https://www.tiktok.com/@magomeal" target="_blank" rel="noopener noreferrer" className="social-icon tiktok-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* 🌿 Hero Section with Animated Text and Product Images */}
      <section className="hero-products-section">
        <div className="container">
          {/* MagoMeal Logo */}
          <div className={`hero-logo-container ${isVisible ? 'animate-fade-in' : ''}`}>
            <img
              src={logoSvg}
              alt="MagoMeal Logo"
              className="hero-logo"
            />
          </div>
          {/* Animated Description */}
          <p className={`hero-description ${isVisible ? 'animate-fade-up' : ''}`}>
            {t('home.heroDescription')}
          </p>

          {/* Product Category Images */}
          <div className={`hero-products-grid ${isVisible ? 'animate-zoom-in' : ''}`}>
            {/* Edible Cups Image */}
            <Link to="/edible-cups" className="hero-product-card">
              <div className="hero-product-image-container">
                <img
                  src={heroCupsImg}
                  alt={t('home.edibleCups')}
                  className="hero-product-image"
                />
                <div className="hero-product-overlay">
                  <span className="hero-product-label">{t('home.edibleCups')}</span>
                  <span className="hero-product-cta">{t('home.explore')} →</span>
                </div>
              </div>
            </Link>

            {/* Cutlery & Tableware Image */}
            <Link to="/cutlery-tableware" className="hero-product-card">
              <div className="hero-product-image-container">
                <img
                  src={heroCutleryImg}
                  alt={t('home.cutleryTableware')}
                  className="hero-product-image"
                />
                <div className="hero-product-overlay">
                  <span className="hero-product-label">{t('home.cutleryTableware')}</span>
                  <span className="hero-product-cta">{t('home.explore')} →</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 🔆 Summer Sale banner */}
      <SummerSale />

      {/* ✅ Expert Endorsement Section */}
      <section className="endorsement-section">
        <div className="container">
          <div className="endorsement-header">
            <span className="endorsement-badge">Expert Approved</span>
            <h2 className="endorsement-title">Trusted by Coffee Professionals</h2>
            <p className="endorsement-subtitle">
              Discover what industry experts say about our revolutionary edible cups
            </p>
          </div>

          <div className="endorsement-content centered-layout">
            {/* Left Side - Expert Profile */}
            <div className="endorsement-profiles">
              {/* Simone Bianchi Card */}
              <div className="expert-card">
                <div className="expert-image-wrapper">
                  <img
                    src={simoneImg}
                    alt="Simone Bianchi"
                    className="expert-image"
                  />
                  <div className="expert-badge-overlay">
                    <span>☕ Coffee Expert</span>
                  </div>
                </div>
                <div className="expert-details">
                  <h3 className="expert-name">Simone Bianchi</h3>
                  <div className="expert-credentials">
                    <span className="credential-tag highlight">🏆 ESTA Dutch Tea Tasting 2026 & 2024</span>
                    <span className="credential-tag">🥉 4th Dutch CupTaster 2023</span>
                    <span className="credential-tag">☕ Coffee Specialist</span>
                    <span className="credential-tag">🍸 Mixologist</span>
                    <span className="credential-tag">🍷 Wine Sommelier</span>
                    <span className="credential-tag">
                      👨‍🍳 Head Barista at{' '}
                      <a href="https://www.dakcoffeeroasters.com/" target="_blank" rel="noopener noreferrer">
                        DAK Coffee Roasters
                      </a>
                    </span>
                  </div>
                  <blockquote className="expert-quote">
                    <span className="quote-mark">"</span>
                    I really liked this edible cup. It tastes good, is vegan, and is fun to eat after the drink.
                    <span className="quote-mark">"</span>
                  </blockquote>
                </div>
              </div>
            </div>

            {/* Right Side - Video */}
            <div className="endorsement-video-wrapper">
              <div className="video-container">
                <video
                  className="endorsement-video"
                  controls
                  playsInline
                  poster={simoneImg}
                >
                  <source
                    src={simoneVideo}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
                <div className="video-caption">
                  <span className="video-icon">▶</span>
                  <div className="video-caption-text">
                    <span>Watch Simone's Experience</span>
                    <span className="video-credit">Created by Diniz Janssen</span>
                  </div>
                </div>
              </div>
              <div className="sustainability-note">
                <Leaf size={20} />
                <span>Sustainable • Vegan • Delicious</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ Edible Cups Section */}
      <section className="product-category-section">
        <div className="container">
          <div className="category-header">
            <h2 className="category-title">{t('home.edibleCups')}</h2>
            <p className="category-subtitle">{t('home.edibleCupsSubtitle')}</p>
          </div>
          <div className="network-grid">
            {edibleCupsFeatures.map((feature, index) => (
              <div key={index} className="network-card hover-lift feature-card-with-leafs">
                <div className="feature-icon" style={{ color: 'var(--brand-dark)', marginBottom: 'var(--spacing-small)' }}>
                  {feature.icon}
                </div>
                <h3 className="network-card-title">{feature.title}</h3>
                <p className="network-card-content">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="category-cta">
            <Link to="/edible-cups" className="btn-primary">
              {t('home.exploreEdibleCups')}
            </Link>
          </div>
          {/* Edible Cups Image Carousel */}
          <HomeCarousel
            images={edibleCupsCarouselImages}
            autoPlayInterval={2500}
            title={t('home.exploreEdibleCups')}
          />
        </div>
      </section>

      {/* ✅ Cutlery & Tableware Section */}
      <section className="product-category-section cutlery-section">
        <div className="container">
          <div className="category-header">
            <h2 className="category-title">{t('home.cutleryTableware')}</h2>
            <p className="category-subtitle">{t('home.cutlerySubtitle')}</p>
          </div>
          <div className="network-grid">
            {cutleryFeatures.map((feature, index) => (
              <div key={index} className="network-card hover-lift feature-card-with-leafs">
                <div className="feature-icon" style={{ color: 'var(--brand-dark)', marginBottom: 'var(--spacing-small)' }}>
                  {feature.icon}
                </div>
                <h3 className="network-card-title">{feature.title}</h3>
                <p className="network-card-content">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="category-cta">
            <Link to="/cutlery-tableware" className="btn-primary">
              {t('home.exploreCutlery')}
            </Link>
          </div>
          {/* Cutlery & Tableware Image Carousel */}
          <HomeCarousel
            images={cutleryCarouselImages}
            autoPlayInterval={2500}
            title={t('home.exploreCutlery')}
          />
        </div>
      </section>

      {/* Collaboration Section - Completely Redesigned */}
      <section className="collaboration-section">
        <div className="container">
          {/* Section Header */}
          <div className="collaboration-header">
            <span className="section-badge">
              <Leaf size={16} />
              Partner With Us
            </span>
            <h2 className="collaboration-title">
              Ready to Go Green? <span className="highlight">Let's Collaborate</span>
            </h2>
          </div>

          {/* Main Content */}
          <div className="collaboration-content">
            {/* Text Content - Unified styling with highlighted keywords */}
            <div className="collaboration-text">
              <p className="collaboration-paragraph">
                Whether you run a <span className="keyword-highlight" data-delay="0">restaurant</span> looking to elevate the dining experience with sustainable tableware,
                a <span className="keyword-highlight" data-delay="100">hotel</span> seeking eco-friendly guest solutions, or a <span className="keyword-highlight" data-delay="200">museum</span> creating memorable exhibition moments, our
                edible cups and cutlery fit naturally into your setting.
              </p>

              <p className="collaboration-paragraph">
                <span className="keyword-highlight" data-delay="300">Coffee shops</span> can stand out with innovative, waste-free drinkware that attracts environmentally
                conscious customers. Brands can create memorable activations and experiential campaigns by
                incorporating edible elements that people actually interact with.
              </p>

              <p className="collaboration-paragraph">
                From <span className="keyword-highlight" data-delay="400">festivals</span> and markets to community events, our products add a sustainable, conversation-starting
                touch. <span className="keyword-highlight" data-delay="500">Corporate functions and office gatherings</span> become an opportunity to demonstrate real commitment
                to sustainability while leaving a strong impression on clients and teams.
              </p>

              <p className="collaboration-paragraph special-moment">
                For <span className="keyword-highlight" data-delay="600">life's special moments</span> (weddings, birthdays, and private celebrations), edible tableware turns
                something ordinary into something unforgettable.
              </p>

              <p className="collaboration-paragraph closing">
                Wherever people gather, we believe great experiences and responsible choices should go hand in hand.
              </p>
            </div>

            {/* Use Cases Carousel */}
            <div className="use-cases-section">
              <h3 className="use-cases-title">Perfect For Every Setting</h3>

              <div className="use-cases-carousel-wrapper">
                <button
                  className="carousel-nav-btn prev"
                  onClick={() => scrollCarousel('left')}
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={20} />
                </button>

                {/* Horizontal Scrolling Carousel */}
                <div className="use-cases-carousel" ref={useCaseCarouselRef}>
                  {useCases.map((useCase, index) => (
                    <div
                      key={useCase.id}
                      className={`use-case-card ${activeUseCase === index ? 'active' : ''}`}
                      onClick={() => setActiveUseCase(index)}
                      style={{ '--accent-color': useCase.color }}
                    >
                      <div className="use-case-image-wrapper">
                        <img
                          src={useCase.image}
                          alt={useCase.label}
                          className="use-case-image"
                          loading="lazy"
                        />
                        <div className="use-case-overlay">
                          <div className="use-case-icon">{useCase.icon}</div>
                        </div>
                      </div>
                      <div className="use-case-content">
                        <h4 className="use-case-label">{useCase.label}</h4>
                        <p className="use-case-description">{useCase.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  className="carousel-nav-btn next"
                  onClick={() => scrollCarousel('right')}
                  aria-label="Scroll right"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Progress Dots */}
              <div className="use-cases-dots">
                {useCases.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${activeUseCase === index ? 'active' : ''}`}
                    onClick={() => scrollToCard(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* CTA Buttons - Using standard site button classes */}
          <div className="collaboration-cta">
            <Link to="/contact" className="btn-primary">
              Start a Collaboration <ArrowRight size={18} style={{ marginLeft: '8px' }} />
            </Link>
            <Link to="/shop" className="btn-secondary">
              Order Now <Leaf size={18} style={{ marginLeft: '8px' }} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;